import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User as AppUser } from '@/models/UserTypes';
import { useDevTools } from '@/hooks/useDevTools';

interface DevStudentViewProps {
  onActivateStudentView: (user: AppUser) => void;
  isActive: boolean;
  onDeactivate: () => void;
}

export const DevStudentView: React.FC<DevStudentViewProps> = ({
  onActivateStudentView,
  isActive,
  onDeactivate
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [showActivationFeedback, setShowActivationFeedback] = useState(false);
  const [mockUser, setMockUser] = useState<AppUser | null>(null);
  const { showDevTools } = useDevTools();

  // Don't show in production environments
  const isDevelopment = (process.env.NODE_ENV === 'development' ||
                        window.location.hostname === 'localhost') &&
                       !window.location.hostname.includes('vercel') &&
                       !window.location.hostname.includes('netlify') &&
                       !window.location.hostname.includes('socialworktestprepacademy.com');

  // Dynamically import mockUser only in development
  React.useEffect(() => {
    if (isDevelopment && showDevTools && !mockUser) {
      import('@/utils/mockTestData').then(({ mockUser: importedMockUser }) => {
        setMockUser(importedMockUser);
      });
    }
  }, [isDevelopment, showDevTools, mockUser]);

  // Only show when dev tools enabled (Ctrl+Shift+D)
  if (!isDevelopment || !showDevTools || !isVisible || !mockUser) return null;

  const handleActivateStudentView = () => {
    onActivateStudentView(mockUser);
    setShowActivationFeedback(true);
    setTimeout(() => setShowActivationFeedback(false), 3000);
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleResetSession = () => {
    onDeactivate();
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
  };

  return (
    <>
      {/* Floating Dev Panel */}
      <div 
        style={{ 
          position: 'fixed', 
          bottom: '1rem', 
          left: '1rem', 
          zIndex: 9998,
          maxHeight: '80vh',
          overflowY: 'auto',
          maxWidth: '320px'
        }}
      >
        <div className="bg-warning-100 border-2 border-warning-400 rounded-lg p-3 shadow-ledge-lg">
          <div className="flex flex-col space-y-2">
            <div className="text-xs font-semibold text-warning-700 mb-1">
              🚧 DEV MODE
            </div>
            
            {!isActive ? (
              <button
                onClick={handleActivateStudentView}
                className="bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium py-2 px-3 rounded transition-colors"
                title="View app as active student with realistic test data"
              >
                👨‍🎓 Activate Student View
              </button>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-success font-medium">
                  ✅ Student View Active
                </div>
                {location.pathname !== '/dashboard' && (
                  <button
                    onClick={handleGoToDashboard}
                    className="bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium py-1 px-3 rounded transition-colors w-full"
                  >
                    📊 View Dashboard
                  </button>
                )}
                <button
                  onClick={onDeactivate}
                  className="bg-error-500 hover:bg-error-600 text-white text-xs font-medium py-1 px-3 rounded transition-colors w-full"
                >
                  Exit Student View
                </button>
              </div>
            )}
          
            <div className="text-xs text-neutral-600 max-w-48">
              <strong>Mock Student:</strong><br />
              {mockUser.profile?.name}<br />
              {mockUser.email}<br />
              <em>All test data & realistic metrics</em>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={handleResetSession}
                className="text-xs bg-secondary-500 hover:bg-secondary-600 text-white px-2 py-1 rounded"
                title="Clear all data and reset to normal view"
              >
                🔄 Reset All
              </button>
              <button
                onClick={() => setIsVisible(false)}
                className="text-xs text-neutral-500 hover:text-neutral-700 underline"
              >
                Hide Panel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Top Status Bar - Only show when student view is active */}
      {isActive && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 9998 
          }}
          className="bg-accent-600 text-white text-xs py-2 px-4 shadow-ledge-lg"
        >
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center space-x-3">
              <span className="font-semibold">🚧 DEV: Student View Active</span>
              <span className="opacity-80">•</span>
              <span>Viewing as: {mockUser.profile?.name} ({mockUser.email})</span>
              <span className="opacity-80">•</span>
              <span className="text-primary-200">✓ {mockUser.totalQuestionsAttempted} questions answered</span>
            </div>
            <button
              onClick={onDeactivate}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-xs transition-colors"
            >
              Exit Student View
            </button>
          </div>
        </div>
      )}

      {/* Activation Feedback Toast */}
      {showActivationFeedback && (
        <div 
          style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            zIndex: 10000 
          }}
          className="bg-success text-white px-6 py-3 rounded-lg shadow-ledge-lg animate-pulse"
        >
          <div className="text-center">
            <div className="text-sm font-semibold">✅ Student View Activated!</div>
            <div className="text-xs mt-1">You are now viewing as {mockUser.profile?.name}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default DevStudentView;